import './styles.scoped.css'
import {useState} from 'react'
import {Typography, Button, IconButton, Menu, MenuItem, Skeleton} from '@mui/material'
import {Add, MoreHoriz} from '@mui/icons-material'
import {TextSkeleton} from '../Skeletons'
import WorkExperienceDialog from '../WorkExperienceDialog'

let nextId = 0

const monthIndex = (year, month) => Number(year) * 12 + Number(month) - 1

export const formatDuration = (experience, now = new Date()) => {
    const end = experience.currentlyWorking ? now.getFullYear() * 12 + now.getMonth() : monthIndex(experience.endYear, experience.endMonth)
    const totalMonths = Math.max(0, end - monthIndex(experience.startYear, experience.startMonth))
    const years = Math.floor(totalMonths / 12)
    if (years > 0) return `${years} ${years === 1 ? 'year' : 'years'}`
    const months = Math.max(1, totalMonths)
    return `${months} ${months === 1 ? 'month' : 'months'}`
}

export const formatPeriod = experience => `${experience.startYear} - ${experience.currentlyWorking ? 'Present' : experience.endYear}`

// Controlled list of work experiences: value is [{id, jobTitle, company,
// startMonth, startYear, currentlyWorking, endMonth, endYear}]. loading shows placeholder cards while the owner fetches them.
export default ({value = [], onChange, disabled = false, loading = false}) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editing, setEditing] = useState(null)
    const [menu, setMenu] = useState(null)

    const openAdd = () => {
        setEditing(null)
        setDialogOpen(true)
    }

    const openEdit = experience => {
        setMenu(null)
        setEditing(experience)
        setDialogOpen(true)
    }

    const remove = experience => {
        setMenu(null)
        onChange(value.filter(entry => entry.id !== experience.id))
    }

    const save = experience => {
        if (experience.id) {
            onChange(value.map(entry => (entry.id === experience.id ? experience : entry)))
        } else {
            onChange([...value, {...experience, id: `experience-${++nextId}`}])
        }
        setDialogOpen(false)
    }

    return (
        <>
            <div className="title">
                <Typography variant="overline" color="text.secondary">
                    WORK EXPERIENCES
                </Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={openAdd} disabled={disabled || loading}>
                    Add Experience
                </Button>
            </div>
            {loading ? (
                <div className="list">
                    {[0, 1].map(index => (
                        <div className="card" key={index}>
                            <div className="top">
                                <div className="top-left">
                                    <TextSkeleton width={90} fontSize={14} />
                                    <TextSkeleton width={index ? 64 : 56} fontSize={14} />
                                </div>
                                <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                            </div>
                            <TextSkeleton width={index ? 140 : 180} fontSize={16} />
                            <TextSkeleton width={index ? 110 : 130} fontSize={12} />
                        </div>
                    ))}
                </div>
            ) : value.length === 0 ? (
                <Typography variant="body2" color="text.secondary" className="empty">
                    No work experience added yet.
                </Typography>
            ) : (
                <div className="list">
                    {value.map(experience => (
                        <div className="card" key={experience.id}>
                            <div className="top">
                                <div className="top-left">
                                    <Typography variant="h9" color="text.primary">
                                        {formatPeriod(experience)}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        ({formatDuration(experience)})
                                    </Typography>
                                </div>
                                <IconButton
                                    aria-label={`Options for ${experience.jobTitle}`}
                                    disabled={disabled}
                                    onClick={e => setMenu({anchor: e.currentTarget, experience})}>
                                    <MoreHoriz />
                                </IconButton>
                            </div>
                            <Typography variant="h8" color="primary">
                                {experience.jobTitle}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.disabled">
                                {experience.company}
                            </Typography>
                        </div>
                    ))}
                </div>
            )}
            <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
                <MenuItem onClick={() => openEdit(menu.experience)}>Edit</MenuItem>
                <MenuItem onClick={() => remove(menu.experience)} sx={{color: 'error.main'}}>
                    Delete
                </MenuItem>
            </Menu>
            <WorkExperienceDialog open={dialogOpen} experience={editing} onClose={() => setDialogOpen(false)} onSave={save} />
        </>
    )
}
